import {
  CloseIcon,
  getWaitingPlaces,
  Pick,
  placeOnDay,
  takePlaceOff,
  type Plan,
  type PlanStop,
  type WaitingPlace,
} from '@trippath/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dayShort } from '../../lib/format';
import { stopDays } from './cityDays';

interface Props {
  trip: string;
  stop: PlanStop;
  /** The plan shown: the places are read again when it changes. */
  plan: Plan;
  /** A change asked of the plan, as the page makes its own. */
  onChange: (asked: Promise<Plan>) => void;
}

/**
 * The places taken in at this stop without a day (the Import mockup): each put on a day becomes
 * a thing to do then, or is taken off the stop and stays among one's own places. None, nothing.
 */
export function WaitingPlaces({ trip, stop, plan, onChange }: Props) {
  const { t, i18n } = useTranslation();
  const [places, setPlaces] = useState<WaitingPlace[]>([]);
  const [asked, setAsked] = useState(0);

  useEffect(() => {
    let live = true;
    getWaitingPlaces(trip).then(
      (all) => {
        if (live) setPlaces(all.filter((place) => place.stop === stop.id));
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [trip, stop.id, plan, asked]);

  if (places.length === 0) return null;
  return (
    <section className="waiting-places" aria-labelledby="waiting-places">
      <h3 id="waiting-places">{t('importPlaces.waiting')}</h3>
      <p className="quiet">{t('importPlaces.waitingHint')}</p>
      <ul>
        {places.map((place) => (
          <li key={place.id}>
            <span className="waiting-name">
              {place.name}
              {place.address !== null && <small>{place.address}</small>}
            </span>
            <Pick
              label={t('importPlaces.pickDay', { name: place.name })}
              quiet
              searchable={false}
              placeholder={t('importPlaces.onDay')}
              value={null}
              options={stopDays(stop).map((day) => ({
                value: day,
                label: dayShort(day, i18n.language),
              }))}
              onChange={(day) => {
                onChange(placeOnDay(trip, place.id, stop.id, day));
              }}
            />
            <button
              type="button"
              className="icon-button icon-button-small"
              aria-label={t('importPlaces.takeOff', { name: place.name })}
              title={t('importPlaces.takeOff', { name: place.name })}
              onClick={() => {
                takePlaceOff(trip, place.id, stop.id).then(
                  () => {
                    setAsked((n) => n + 1);
                  },
                  () => undefined,
                );
              }}
            >
              <CloseIcon />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
